import { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface FocusSectionNavProps {
  sections: { key: string; title: string }[];
  activeKey: string | undefined;
  onSelect: (key: string) => void;
}

/**
 * Horizontal section selector for focus mode — the header of a
 * fullscreen publication editor needs to let the user move between
 * sections without leaving fullscreen. Given there are typically only a
 * handful of sections in a publication, a chevron-guided strip that's
 * visible exactly when needed (chevrons hidden when there's no
 * overflow) is the simplest usable form.
 */
export function FocusSectionNav({ sections, activeKey, onSelect }: FocusSectionNavProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // On a fresh mount, scroll the active section into view. The
  // `overflow-x-auto` strip respects its own scroll offset rather than
  // page-level scrolling, so without this it could be at a position
  // that hides the actually-selected section.
  useEffect(() => {
    const active = scrollRef.current?.querySelector<HTMLButtonElement>('[data-active="true"]');
    active?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [activeKey, sections]);

  const scrollBy = (amount: number) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto" ref={scrollRef}>
      <ChevronLeft className="size-4 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 gap-1">
        {sections.map((section) => (
          <button
            key={section.key}
            type="button"
            data-active={section.key === activeKey}
            onClick={() => onSelect(section.key)}
            className={`shrink-0 rounded-md px-2.5 py-1 text-xs transition-colors ${
              section.key === activeKey
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:bg-accent/50'
            }`}
          >
            {section.title || 'Untitled section'}
          </button>
        ))}
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </div>
  );
}
